import { useTheme } from '@material-ui/core';
import { isEqual } from 'lodash';
import { useCallback } from 'react';
import { WbAutocomplete, WbChip } from '../WbTextField';
import { FieldActions } from './FieldActions';
import { useGeneralPurposePickerContext } from './GeneralPurposePickerContext';
import { BaseProps, KindEnum, TextFieldValue } from './types';
import { WbComponentsListArray } from '../WbComponentsListArray';

export const MultiTextField = ({
  title,
  description,
  freeSolo,
  values,
  disabled,
  required,
  selectableEntities,
  ...props
}: {
  title: string;
  description?: string | undefined;
  freeSolo: boolean;
  values: TextFieldValue[];
  disabled: boolean;
  required: boolean;
  selectableEntities: number;
} & BaseProps) => {
  const { setSelectedValues, setOpenDialog } = useGeneralPurposePickerContext();
  const theme = useTheme();

  const onClear = useCallback(() => {
    setSelectedValues({ tableValues: [], arbitraryValues: [] });
  }, [setSelectedValues]);

  const handleDelete = (option: TextFieldValue) => () => {
    setSelectedValues(s => ({
      tableValues: s.tableValues.filter(v => !isEqual(v, option)),
      arbitraryValues: s.arbitraryValues?.filter(v => !isEqual(v, option)),
    }));
  };

  const adaptLabel = (option: TextFieldValue) => {
    if (option?.kind === KindEnum.arbitrary) return option?.label;
    const labelKind = option?.kindDisplayName ?? option?.kind;

    return `${labelKind && selectableEntities > 1 ? `${labelKind}:` : ''} ${
      option?.label
    }`;
  };

  const helperText = description;

  return (
    <WbAutocomplete<any, true, false, any>
      multiple
      fullWidth
      disabled={disabled}
      required={required}
      freeSolo={freeSolo}
      label={title}
      onOpen={() => {
        if (!freeSolo) {
          setOpenDialog(true);
        }
      }}
      options={[]}
      helperText={helperText}
      value={values || []}
      onChange={(_event, newValue) => {
        if (!newValue.length) {
          onClear();
          return;
        }

        const newValueAdapted = newValue?.reduce(
          (acc, curr) => {
            if (typeof curr === 'string') {
              acc.arbitraryValues.push({
                kind: KindEnum.arbitrary,
                label: curr,
                value: curr,
              });
            } else {
              if (curr.kind === KindEnum.arbitrary) {
                acc.arbitraryValues.push(curr);
              } else {
                acc.tableValues.push(curr);
              }
            }

            return acc;
          },
          { tableValues: [], arbitraryValues: [] },
        );

        setSelectedValues(newValueAdapted);
      }}
      renderTags={(chips: TextFieldValue[], getTagProps) => {
        return (
          <WbComponentsListArray
            gap={8}
            renderContainer={(children, { totalWidth }) => (
              <div
                style={{
                  minWidth: 50,
                  overflow: 'hidden',
                  flexGrow: 2,
                  flexShrink: 1,
                  // when we know the total size all the elements occupy, we set it as maxWidth,
                  // there would be no point in making the container larger than that
                  maxWidth: totalWidth !== null ? totalWidth + 5 : undefined,
                }}
              >
                {children}
              </div>
            )}
            children={chips.map((option: TextFieldValue, index: number) => (
              <WbChip
                label={adaptLabel(option)}
                {...getTagProps({ index })}
                key={adaptLabel(option)}
                // remove classes added by MUI
                className={undefined}
                // remove margin for WbComponentsListArray to work correctly
                style={{
                  margin: 0,
                  backgroundColor:
                    option?.kind === KindEnum.arbitrary
                      ? theme.palette.bkg.secondary
                      : theme.palette.bkg.primary,
                }}
                onDelete={handleDelete(option)}
              />
            ))}
          />
        );
      }}
      {...props}
      InputProps={{
        endAdornment: !disabled ? (
          <FieldActions onClear={onClear} onOpen={() => setOpenDialog(true)} />
        ) : undefined,
        ...props?.InputProps,
        style: {
          flexWrap: 'nowrap',
          ...props?.InputProps?.style,
        },
      }}
    />
  );
};
